import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import AvatarWithName from '@components/avatar-with-name/AvatarWithName';
import { FEATURE_FLAGS } from '@constants/feature';
import { AccountType, OrgStatus } from '@constants/generic';
import {
  INDIVIDUAL_PROFILE_ITEMS,
  ORG_PROFILE_SECTIONS
} from '@constants/sideMenu';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import useCheckPermission from '@hooks/checkPermission';
import ProfileOptions from '@layouts/side-nav-bar/ProfileOptions';
import { getPhoneNumberWith91 } from '@utils/generic';

import { ProfileSettingsProps } from './types';

import theme from '../../../themes/colors';

const ProfileSettings = (props: ProfileSettingsProps) => {
  const { orgList, subscriptionData, accountMethods } = props;
  const { checkPermission } = useCheckPermission();
  const { currentOrg, currentUser, accountType } = useSelector(
    (state: any) => state.user
  );
  const { orgFeatureFlags } = useSelector((state: any) => state.user);

  const menuItems =
    accountType.type === AccountType.ORGANIZATION
      ? ORG_PROFILE_SECTIONS
      : INDIVIDUAL_PROFILE_ITEMS;

  const isExcludedSideNavItem = (id: string) => {
    if (id === 'subscriptions')
      return (
        currentOrg?.status === OrgStatus.VERIFICATION_PENDING ||
        currentOrg?.status === OrgStatus.VERIFICATION_SUBMITTED
      );

    if (id === 'docs')
      return !(
        orgFeatureFlags &&
        orgFeatureFlags.includes(FEATURE_FLAGS.API_INTEGRATIONS)
      );

    if (id === 'org-dashboard') return !!currentOrg?.has_multiple_currencies;

    return false;
  };

  const profileItemClick = (label: string) => {
    sendMixpanelEvent(Event.SIDEBAR_TAB_CLICKED, {
      section: label
    });
  };

  // Helper to filter and exclude items in a section
  const getPermittedSectionItems = (section: any[]) =>
    section.filter(
      (navItem: any) =>
        (!navItem?.permissions ||
          navItem?.permissions.some((permission: any) =>
            checkPermission([permission])
          )) &&
        (!navItem?.featureFlag ||
          orgFeatureFlags?.includes(navItem?.featureFlag)) &&
        !isExcludedSideNavItem(navItem.id)
    );

  const renderOrgProfileSections = () =>
    menuItems.map((section, sectionIdx) => {
      const permittedItems = getPermittedSectionItems(section);
      if (permittedItems.length === 0) return null;
      const sectionKey =
        permittedItems[0]?.id ||
        JSON.stringify(permittedItems.map((i) => i.id));
      let showDivider = false;
      if (sectionIdx < ORG_PROFILE_SECTIONS.length - 1) {
        const nextSection = ORG_PROFILE_SECTIONS[sectionIdx + 1];
        const nextPermitted = getPermittedSectionItems(nextSection);
        showDivider = nextPermitted.length > 0;
      }
      return (
        <div key={sectionKey}>
          {permittedItems.map((item) => {
            const { id, Icon: IconComponent, label, route } = item;
            return (
              <Link
                key={id}
                to={route}
                className="mx-1 flex items-center gap-3 rounded-md px-4 py-2.5 text-sm text-cofeeNeutral10 hover:bg-cofeeGreyBlue75"
                onClick={() => profileItemClick(label)}
              >
                {IconComponent && (
                  <IconComponent className="size-5 !text-cofeeNeutral10" />
                )}
                {item.label}
              </Link>
            );
          })}
          {showDivider && <div className="h-px w-full bg-cofeeNeutral5" />}
        </div>
      );
    });

  return (
    <div className="absolute right-2.5 top-16 z-20 w-60 min-w-48 cursor-default overflow-hidden rounded-2xl bg-cofeeNeutral1 text-sm font-normal shadow-md">
      <div className="border-b border-b-cofeeNeutral5 p-4">
        <AvatarWithName
          name={currentUser.name}
          subText={getPhoneNumberWith91(currentUser.mobile)}
          bgColor={theme.cofeeGreen1}
          wrapperClass="!gap-2"
          avatarClass="h-[50px] w-[50px] min-w-[50px] text-xl text-white"
          nameClass="!text-sm text-cofeeNeutral10 font-semibold break-words"
          subTextClass="text-cofeeNeutral8 mt-2"
          textWrapperClass="!items-start"
        />
      </div>
      <div className="my-1">{renderOrgProfileSections()}</div>
      <ProfileOptions
        orgList={orgList}
        accountMethods={accountMethods}
        subscriptionData={subscriptionData}
        labelStyle="font-medium text-cofeeNeutral10"
        isProfileList={false}
      />
    </div>
  );
};

export default ProfileSettings;
